/**
 * Vanilla JS wrapper for the ChatWidget.
 * Renders a standalone chat widget without React dependency.
 * Used in CDN distribution for non-React sites.
 */
import { UITLApiClient } from '../api';
import type { UITLConfig, ChatMessage } from '../types';

export class VanillaChatWidget {
  private api: UITLApiClient;
  private container: HTMLElement;
  private isOpen = false;
  private sessionId: number | null = null;
  private messages: ChatMessage[] = [];
  private loading = false;
  private personaName = 'Support Assistant';
  private welcomeMessage = 'Hi! How can I help you today?';
  private primaryColor: string;

  constructor(config: UITLConfig) {
    this.api = new UITLApiClient(config);
    this.primaryColor = config.theme?.primaryColor || '#2563EB';
    this.container = document.createElement('div');
    this.container.id = 'uitl-chat-widget';
    document.body.appendChild(this.container);
    this.loadConfig();
    this.render();
  }

  private async loadConfig() {
    try {
      const cfg = await this.api.getConfig();
      if (cfg.chat) {
        this.personaName = cfg.chat.persona_name || this.personaName;
        this.welcomeMessage = cfg.chat.welcome_message || this.welcomeMessage;
      }
      this.render();
    } catch (e) {
      console.warn('[UITL] Failed to load chat config:', e);
    }
  }

  private async startSession() {
    if (this.sessionId) return;
    try {
      const session = await this.api.createChatSession('help');
      this.sessionId = session.session_id;
      this.messages = session.messages || [];
    } catch (e) {
      console.error('[UITL] Failed to start chat session:', e);
    }
  }

  private async sendMessage(text: string) {
    if (!text.trim() || this.loading) return;
    if (!this.sessionId) await this.startSession();
    if (!this.sessionId) return;

    this.messages.push({ role: 'user', content: text, timestamp: new Date().toISOString() });
    this.loading = true;
    this.render();

    try {
      const res = await this.api.sendChatMessage(this.sessionId, text);
      this.messages.push({ role: 'assistant', content: res.reply, timestamp: new Date().toISOString() });
    } catch (e) {
      this.messages.push({ role: 'assistant', content: 'Sorry, something went wrong. Please try again.', timestamp: new Date().toISOString() });
    }
    this.loading = false;
    this.render();
    this.scrollToBottom();
  }

  private scrollToBottom() {
    const list = this.container.querySelector('#uitl-chat-messages');
    if (list) list.scrollTop = list.scrollHeight;
  }

  private toggle() {
    this.isOpen = !this.isOpen;
    if (this.isOpen && !this.sessionId) this.startSession();
    this.render();
    if (this.isOpen) {
      this.scrollToBottom();
      const input = this.container.querySelector('#uitl-chat-input') as HTMLInputElement;
      input?.focus();
    }
  }

  private render() {
    const c = this.primaryColor;
    if (!this.isOpen) {
      this.container.innerHTML = `
        <button id="uitl-chat-fab" style="position:fixed;bottom:24px;right:24px;width:56px;height:56px;border-radius:50%;background:${c};color:white;border:none;cursor:pointer;font-size:24px;box-shadow:0 4px 12px rgba(0,0,0,0.3);z-index:99999;display:flex;align-items:center;justify-content:center;">💬</button>
      `;
      this.container.querySelector('#uitl-chat-fab')?.addEventListener('click', () => this.toggle());
      return;
    }

    const msgsHtml = this.messages.map(m => {
      const isUser = m.role === 'user';
      return `<div style="display:flex;justify-content:${isUser ? 'flex-end' : 'flex-start'};margin-bottom:8px;">
        <div style="max-width:80%;padding:8px 12px;border-radius:12px;font-size:14px;line-height:1.4;background:${isUser ? c : '#f3f4f6'};color:${isUser ? 'white' : '#1f2937'};">${this.escapeHtml(m.content)}</div>
      </div>`;
    }).join('');

    const loadingHtml = this.loading ? `<div style="display:flex;justify-content:flex-start;margin-bottom:8px;"><div style="padding:8px 12px;border-radius:12px;background:#f3f4f6;color:#9ca3af;font-size:14px;">Thinking...</div></div>` : '';

    this.container.innerHTML = `
      <div style="position:fixed;bottom:24px;right:24px;width:380px;height:520px;background:white;border-radius:16px;box-shadow:0 8px 30px rgba(0,0,0,0.2);display:flex;flex-direction:column;z-index:99999;overflow:hidden;">
        <div style="background:${c};color:white;padding:16px;display:flex;justify-content:space-between;align-items:center;">
          <span style="font-weight:600;font-size:16px;">${this.escapeHtml(this.personaName)}</span>
          <button id="uitl-chat-close" style="background:none;border:none;color:white;font-size:20px;cursor:pointer;">✕</button>
        </div>
        <div id="uitl-chat-messages" style="flex:1;overflow-y:auto;padding:16px;">
          ${this.messages.length === 0 ? `<div style="text-align:center;color:#9ca3af;padding:20px;font-size:14px;">${this.escapeHtml(this.welcomeMessage)}</div>` : ''}
          ${msgsHtml}
          ${loadingHtml}
        </div>
        <div style="padding:12px;border-top:1px solid #e5e7eb;display:flex;gap:8px;">
          <input id="uitl-chat-input" type="text" placeholder="Type a message..." style="flex:1;padding:8px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;outline:none;" ${this.loading ? 'disabled' : ''} />
          <button id="uitl-chat-send" style="padding:8px 16px;background:${c};color:white;border:none;border-radius:8px;cursor:pointer;font-size:14px;" ${this.loading ? 'disabled' : ''}>Send</button>
        </div>
      </div>
    `;

    this.container.querySelector('#uitl-chat-close')?.addEventListener('click', () => this.toggle());
    const input = this.container.querySelector('#uitl-chat-input') as HTMLInputElement;
    const send = this.container.querySelector('#uitl-chat-send');
    const doSend = () => { if (input?.value) { this.sendMessage(input.value); input.value = ''; } };
    send?.addEventListener('click', doSend);
    input?.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') doSend(); });
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  destroy() {
    this.container.remove();
  }
}
